import { Loading, Panel } from "@appkit4/react-components";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { sum } from "lodash";
import { useContext, useEffect, useState } from "react";
import { AutoSizer, List } from "react-virtualized";
import CompanyPanel from "../components/CompanyPanel";
import toast from "../components/DismissibleToast";
import { BackButton, NextButton } from "../components/ProgressNav";
import { Button } from "../components/ReadonlyAwareInputs";
import RoundedBorder from "../components/ui/RoundedBorder";
import { AuthContext } from "../services/AuthProvider";
import getReviewCalc1Values from "../services/calculations/calc1";
import { calculateComponentTotals } from "../services/calculations/ComponentTotals";
import { getExcessDebtCapPriorPeriod } from "../services/calculations/excessDebtCap";
import {
  canProgressCalculation,
  disableProgressInViewMode,
  getCurrentPeriodIsReadonly,
  getGroupData,
  getPoaIndex,
  getSelectedPeriod,
  setGroupData,
} from "../services/GroupContext";
import sqlService from "../services/sqldatabase/sqldatabase.service";
import {
  getAdjustedCapKey,
  getCompanyAccountingPeriodKey,
  getIncludedAdjCaps,
} from "../utils/adjustedCapsUtil";
import { processDateJS } from "../utils/dateProcessor";
import EditAutomaticAllocations, {
  applyAdjustedCapDTR,
  getCalculationGroupValues,
} from "./EditAutomaticAllocations";
import ReviewCalcNav from "./revCalcNavigation";
import RevCalcHeader from "./ReviewCalcHeader";
dayjs.extend(utc);

const ReviewCalc1 = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isPosting, setIsPosting] = useState(false);
  const [isEditingAdjustment, setEditingAdjustment] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState();
  const selectedPeriod = getSelectedPeriod();
  const { period_start, period_end } = selectedPeriod;
  const poaIndex = getPoaIndex();
  const auth = useContext(AuthContext);
  const [adjusted_caps, set_adjusted_caps] = useState();
  const [updateTableValues, setUpdateTableValues] = useState(false);
  const [tableData, setTableData] = useState([]);

  const rowHeight = 55; // Define the height of each row
  const [listHeight, setListHeight] = useState(rowHeight);

  useEffect(() => {
    setListHeight(
      Math.min(tableData.length * rowHeight, window.innerHeight * 0.78),
    );
  }, [tableData]);

  const rowRenderer = ({ index, key, style }) => {
    const row = tableData[index];

    return (
      <div
        key={key}
        role="row"
        style={{
          ...style,
          display: "grid",
          gridTemplateColumns: "2fr 2fr 1fr 2fr 2fr 1fr 1fr",
          alignItems: "center",
          borderBottom: "1px solid #f0f0f0",
          padding: "8px 12px",
        }}
      >
        <div>{row?.company_name || "N/A"}</div>
        <div>{row?.accounting_period || "N/A"}</div>
        <div>{row?.currency || "N/A"}</div>
        <div>
          {row?.net_tax_interest_expense
            ? Math.round(Number(row?.net_tax_interest_expense)).toLocaleString()
            : "0"}
        </div>
        <div>
          {row?.tax_ebitda
            ? Math.round(Number(row?.tax_ebitda)).toLocaleString()
            : "0"}
        </div>
        <div>{row?.status || "N/A"}</div>
        <div>
          <Button
            viewEditButton
            neverReadonly
            onClick={() => {
              setSelectedCompany(row);
              setEditingAdjustment(row.edit);
            }}
            disabled={!row.edit}
          />
        </div>
      </div>
    );
  };

  const isReadOnlyOrViewPoA =
    auth?.isReadOnlyUser() || getCurrentPeriodIsReadonly();
  const disableViewModeProgress = disableProgressInViewMode();

  let savedData = getGroupData();
  useEffect(() => {
    setIsLoading(true);
    if (!savedData || !selectedPeriod || poaIndex < 0) {
      return;
    }

    const EDC_poa = getExcessDebtCapPriorPeriod(
      savedData,
      savedData?.periods_of_account[poaIndex],
    );
    const totals_poa = calculateComponentTotals(EDC_poa);
    savedData.periods_of_account[poaIndex] = totals_poa;

    setGroupData(savedData);
  }, []);

  useEffect(() => {
    const calculation_values = isReadOnlyOrViewPoA
      ? getSelectedPeriod().adjusted_caps.filter((cap) => cap !== null)
      : getReviewCalc1Values();
    set_adjusted_caps(getIncludedAdjCaps(calculation_values));

    const data = [];
    calculation_values?.forEach((cap) => {
      if (cap) {
        const company_name = savedData?.companies?.filter(
          (c) => c?.company_ID === cap?.company_ID,
        )[0]?.company_name;

        data?.push({
          ...cap,
          company_name: company_name,
          company_ID: cap?.company_ID,
          doc_id: cap?.doc_id,
          accounting_period:
            processDateJS(cap?.start_date)?.format("DD/MM/YYYY") +
            " - " +
            processDateJS(cap?.end_date)?.format("DD/MM/YYYY"),
          currency: cap?.currency ? cap?.currency : "GBP",
          net_tax_interest_expense: cap?.net_tax_interest_expense
            ? cap?.net_tax_interest_expense
            : 0,
          tax_ebitda: cap?.tax_ebitda ? cap?.tax_ebitda : 0,
          status: cap?.adjustment_status ? cap?.adjustment_status : "N/A",
          edit: cap?.inclusion_factor < 1 ? true : false,
        });
      }
    });
    data?.sort((a, b) =>
      a?.adjustment_status?.localeCompare(b?.adjustment_status),
    );

    setTableData(data);
    setIsLoading(false);
  }, [updateTableValues]);

  const save = (adjusted_caps, company_accounting_periods = null) => {
    if (!selectedPeriod || !period_start || !period_end) {
      toast.error("Failed to find the selected period");
      return;
    }
    if (!savedData || isReadOnlyOrViewPoA) {
      return;
    }
    const poa_index = savedData?.periods_of_account?.findIndex((poa) =>
      processDateJS(poa?.period_start)?.isSame(processDateJS(period_start)),
    );
    if (poa_index === -1) {
      toast.error("Failed to find the data for the selected period");
      return;
    }

    const canProgressCalc = canProgressCalculation();

    const poa = savedData?.periods_of_account[poa_index];
    savedData.periods_of_account[poa_index] = {
      ...poa,
      adjusted_caps,
      ...canProgressCalc,
    };

    if (company_accounting_periods) {
      savedData.company_accounting_periods = company_accounting_periods;
    } else {
      const adjCapsMap = new Map();
      adjusted_caps?.forEach((cap) => {
        const key = getAdjustedCapKey(cap);
        adjCapsMap.set(key, cap);
      });

      const compCapMap = new Map();
      savedData?.company_accounting_periods?.forEach((cap, index) => {
        const key = getCompanyAccountingPeriodKey(cap);
        compCapMap.set(key, { cap, index });
      });

      for (let row of tableData) {
        const lookupKey = getAdjustedCapKey(row);
        const adjCapEntry = adjCapsMap.get(lookupKey);
        const index = adjusted_caps?.indexOf(adjCapEntry);

        const compCapKey = getCompanyAccountingPeriodKey(row);
        const compCapEntry = compCapMap.get(compCapKey);
        const original_cap_index = compCapEntry?.index;
        const original_cap = compCapEntry?.cap;

        if (
          index === -1 ||
          original_cap_index === -1 ||
          !adjCapEntry ||
          !original_cap
        ) {
          console.error(
            `Failed to find current Adjusted Cap or Cap for ${row}`,
          );
          continue;
        }

        const data = adjusted_caps;
        const edited_cap = adjusted_caps[index];

        const calcGroupsToProcess = [
          "ntlr",
          "ntdc",
          "tlr",
          "tdc",
          "ofa",
          "tax_ebitda",
        ];

        const groupedValues = {};
        calcGroupsToProcess.forEach((calcGroup) => {
          groupedValues[calcGroup] = getCalculationGroupValues(
            original_cap,
            row,
            selectedPeriod,
            calcGroup,
            savedData,
          );
        });

        const allGroupValues = calcGroupsToProcess.flatMap((calcGroup) => {
          const groupValues = groupedValues[calcGroup];

          if (calcGroup !== "tax_ebitda") {
            edited_cap[`${calcGroup}_subtotal`] = {
              value: sum(groupValues.map((v) => v.current_cir_amount)),
              manual_adjustment: groupValues.reduce(
                (acc, row) => acc || row.manual_adjustment,
                false,
              ),
            };
          }

          return groupValues;
        });

        original_cap.pre_app_amounts = original_cap.pre_app_amounts ?? {};
        for (const row of allGroupValues) {
          const key = row?.metric_id;
          edited_cap[key] = {
            total: row?.accounting_period_total,
            value: row?.current_cir_amount,
            manual_adjustment: row?.manual_adjustment,
          };
          edited_cap.adjustment_status = row?.manual_adjustment
            ? "Manual Adjustment"
            : edited_cap?.adjustment_status;
          original_cap.pre_app_amounts[key] = {
            value: row?.pre_app_cir_amount,
            manual_adjustment: row.manual_adjustment,
          };
        }

        applyAdjustedCapDTR(edited_cap);

        savedData.company_accounting_periods[original_cap_index] = original_cap;

        data[index] = edited_cap;
      }
    }

    setIsPosting(true);

    const promise = sqlService?.postResponse(savedData);
    const onSuccess = () => {
      setGroupData(savedData);
      setUpdateTableValues(!updateTableValues);
      setIsPosting(false);
    };
    const onError = () => setIsPosting(false);
    return toast.save(promise, onSuccess, onError);
  };

  const editAdjustmentCell = (row, field) => {
    return (
      <Button
        viewEditButton
        neverReadonly
        onClick={() => {
          setSelectedCompany(row);
          setEditingAdjustment(row[field]);
        }}
        disabled={!row[field]}
      />
    );
  };

  if (isLoading)
    return (
      <Loading
        loadingType="linear"
        indeterminate={true}
        compact={false}
        className="page-loader"
      ></Loading>
    );

  if (isEditingAdjustment) {
    return (
      <EditAutomaticAllocations
        navBack={setEditingAdjustment}
        company_period={selectedCompany}
        adjusted_caps={adjusted_caps}
        save={save}
      />
    );
  }
  return (
    <div className="ap-container">
      <div className="ap-container mt-4">
        <CompanyPanel />
      </div>
      <p></p>

      <Panel>
        <RevCalcHeader groupname="Review Calculations - Allocations of Tax-EBITDA and Tax-Interest" />
        <ReviewCalcNav currentStep={0} />
        <p>
          Where a UK Group Company has an accounting period which differs from
          the period of account of the worldwide group and/or has been acquired
          or disposed of during the period, its Tax-EBITDA and Tax-Interest
          amounts must be apportioned between the regarded and disregarded
          accounting periods. A disregarded period is any part of the company’s
          accounting period which falls outside the period of account of the
          worldwide group, or during which the company is not a member of the
          worldwide group.
        </p>
        <p>
          The apportionment must be undertaken on a just and reasonable basis.
          The CIR Analyser automatically calculates the apportionment on a time
          apportionment approach. This may reflect a just and reasonable
          apportionment, but it may also be necessary to amend the automatic
          allocation to reflect a different basis of apportionment - for
          example, where transactions impact only either a regarded or
          disregarded period, the results of those transactions may need to be
          wholly apportioned to the relevant period.
        </p>
        <p>
          The table sets out the Tax-EBITDA and Tax-Interest allocated to the
          regarded part of the relevant accounting period for each UK Group
          Company. Where a company has both regarded and disregarded periods,
          the edit button in the table below is not disabled and the allocations
          can be amended by pressing the “edit” button.
        </p>

        <RoundedBorder>
          {/* Header Row */}
          <div
            role="row"
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 2fr 1fr 2fr 2fr 1fr 1fr",
              alignItems: "center",
              borderBottom: "1px solid #ccc", // Border between header and body
              fontWeight: "bold",
              padding: "8px 12px",
              backgroundColor: "#f5f5f5",
            }}
          >
            <div>Company Name</div>
            <div>Accounting Period</div>
            <div>Currency</div>
            <div>Net Tax Interest Expense</div>
            <div>Tax EBITDA</div>
            <div>Status</div>
            <div>Actions</div>
          </div>

          {/* Virtualized List */}
          <div style={{ height: listHeight, width: "100%" }}>
            <AutoSizer disableHeight>
              {({ width }) => (
                <List
                  width={width}
                  height={listHeight}
                  rowCount={tableData.length}
                  rowHeight={rowHeight}
                  rowRenderer={rowRenderer}
                  overscanRowCount={6}
                />
              )}
            </AutoSizer>
          </div>
        </RoundedBorder>

        <div className="container-fluid mt-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <BackButton disabled={isPosting} />
            </div>
            <div className="d-flex gap-2">
              <Button
                hiddenInReadonly
                loading={isPosting}
                onClick={() => save(adjusted_caps)}
                kind="secondary"
              >
                Save
              </Button>
              <NextButton
                loading={isPosting}
                disabled={disableViewModeProgress}
                preNavigation={() => save(adjusted_caps)}
              />
            </div>
          </div>
        </div>
      </Panel>
    </div>
  );
};

export default ReviewCalc1;
